import React, { useRef, useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'motion/react';

interface Agent {
  id: number;
  name: string;
  x: number;
  y: number;
  health: number;
  maxHealth: number;
  color: string;
  kills: number;
  targetX: number;
  targetY: number;
  isDead: boolean;
  inSafeZone: boolean;
}

interface Zone {
  centerX: number;
  centerY: number;
  radius: number;
  nextCenterX: number;
  nextCenterY: number;
  nextRadius: number;
  shrinkProgress: number;
}

const MAP_SIZE = 2000;
const INITIAL_ZONE_RADIUS = 1000;
const MIN_ZONE_RADIUS = 100;
const SHRINK_INTERVAL = 30000;
const SHRINK_DURATION = 15000;
const POISON_DAMAGE_RATE = 1;
const MIN_SCALE = 1;
const MAX_SCALE = 20;

const Battlefield: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [agents, setAgents] = useState<Agent[]>([]);
  const [zone, setZone] = useState<Zone>({
    centerX: MAP_SIZE / 2,
    centerY: MAP_SIZE / 2,
    radius: INITIAL_ZONE_RADIUS,
    nextCenterX: MAP_SIZE / 2,
    nextCenterY: MAP_SIZE / 2,
    nextRadius: INITIAL_ZONE_RADIUS * 0.6,
    shrinkProgress: 0
  });
  const [hoveredAgent, setHoveredAgent] = useState<Agent | null>(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [gameTime, setGameTime] = useState(0);
  const [aliveCount, setAliveCount] = useState(100);
  const agentsRef = useRef<Agent[]>([]);
  const zoneRef = useRef<Zone>(zone);
  const containerSizeRef = useRef({ width: 0, height: 0 });

  // Initialize agents
  useEffect(() => {
    const newAgents: Agent[] = [];
    for (let i = 1; i <= 100; i++) {
      const x = Math.random() * MAP_SIZE;
      const y = Math.random() * MAP_SIZE;
      newAgents.push({
        id: i,
        name: `Agent_${i.toString().padStart(3, '0')}`,
        x,
        y,
        health: 100,
        maxHealth: 100,
        color: `hsl(${Math.random() * 360}, 70%, 60%)`,
        kills: 0,
        targetX: x,
        targetY: y,
        isDead: false,
        inSafeZone: true
      });
    }
    setAgents(newAgents);
    agentsRef.current = newAgents;
  }, []);

  // Calculate base scale to fit map in container
  const calculateBaseScale = useCallback(() => {
    const container = containerRef.current;
    if (!container) return 1;
    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;
    containerSizeRef.current = { width: containerWidth, height: containerHeight };
    // Scale to fit entire map with some padding
    const scaleX = containerWidth / MAP_SIZE;
    const scaleY = containerHeight / MAP_SIZE;
    return Math.min(scaleX, scaleY) * 0.95; // 95% to leave small padding
  }, []);

  // Initialize scale on mount and resize
  useEffect(() => {
    const baseScale = calculateBaseScale();
    setScale(baseScale);
    
    const handleResize = () => {
      const newBaseScale = calculateBaseScale();
      setScale(prev => {
        // Keep relative zoom level
        const ratio = prev / scale;
        return Math.max(newBaseScale, Math.min(MAX_SCALE * newBaseScale, newBaseScale * ratio));
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [calculateBaseScale]);

  // Game loop
  useEffect(() => {
    const interval = setInterval(() => {
      setGameTime(prev => prev + 100);
      
      setZone(prevZone => {
        const timeSinceLastShrink = gameTime % SHRINK_INTERVAL;
        const isShrinking = timeSinceLastShrink > SHRINK_INTERVAL - SHRINK_DURATION;
        
        let newZone = { ...prevZone };
        
        if (isShrinking) {
          const shrinkProgress = (timeSinceLastShrink - (SHRINK_INTERVAL - SHRINK_DURATION)) / SHRINK_DURATION;
          newZone.shrinkProgress = Math.min(1, shrinkProgress);
          newZone.radius = prevZone.radius + (prevZone.nextRadius - prevZone.radius) * shrinkProgress;
          newZone.centerX = prevZone.centerX + (prevZone.nextCenterX - prevZone.centerX) * shrinkProgress;
          newZone.centerY = prevZone.centerY + (prevZone.nextCenterY - prevZone.centerY) * shrinkProgress;
        } else {
          if (prevZone.shrinkProgress >= 1 || prevZone.radius <= prevZone.nextRadius + 1) {
            newZone.radius = prevZone.nextRadius;
            newZone.centerX = prevZone.nextCenterX;
            newZone.centerY = prevZone.nextCenterY;
            newZone.nextRadius = Math.max(MIN_ZONE_RADIUS, prevZone.nextRadius * 0.6);
            newZone.nextCenterX = Math.max(newZone.nextRadius, Math.min(MAP_SIZE - newZone.nextRadius, 
              prevZone.nextCenterX + (Math.random() - 0.5) * prevZone.nextRadius));
            newZone.nextCenterY = Math.max(newZone.nextRadius, Math.min(MAP_SIZE - newZone.nextRadius, 
              prevZone.nextCenterY + (Math.random() - 0.5) * prevZone.nextRadius));
            newZone.shrinkProgress = 0;
          }
        }
        
        zoneRef.current = newZone;
        return newZone;
      });

      setAgents(prevAgents => {
        const newAgents = [...prevAgents];
        let alive = 0;
        
        newAgents.forEach(agent => {
          if (agent.isDead) return;
          alive++;
          
          const distToZone = Math.sqrt(
            Math.pow(agent.x - zoneRef.current.centerX, 2) + 
            Math.pow(agent.y - zoneRef.current.centerY, 2)
          );
          agent.inSafeZone = distToZone <= zoneRef.current.radius;
          
          if (!agent.inSafeZone) {
            agent.health -= POISON_DAMAGE_RATE * 0.1;
            if (agent.health <= 0) {
              agent.health = 0;
              agent.isDead = true;
              return;
            }
          }
          
          if (!agent.inSafeZone) {
            const dx = zoneRef.current.centerX - agent.x;
            const dy = zoneRef.current.centerY - agent.y;
            const dist = Math.sqrt(dx * dx + dy * dy);
            agent.targetX = agent.x + (dx / dist) * 50;
            agent.targetY = agent.y + (dy / dist) * 50;
          }
          
          const dx = agent.targetX - agent.x;
          const dy = agent.targetY - agent.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          
          if (dist < 5) {
            agent.targetX = Math.random() * MAP_SIZE;
            agent.targetY = Math.random() * MAP_SIZE;
          } else {
            const speed = agent.inSafeZone ? 0.5 : 1.5;
            agent.x += (dx / dist) * speed;
            agent.y += (dy / dist) * speed;
          }
          
          agent.x = Math.max(10, Math.min(MAP_SIZE - 10, agent.x));
          agent.y = Math.max(10, Math.min(MAP_SIZE - 10, agent.y));
        });
        
        newAgents.forEach((agent, i) => {
          if (agent.isDead) return;
          
          const nearby = newAgents.filter((other, j) => 
            i !== j && !other.isDead && 
            Math.abs(agent.x - other.x) < 40 && 
            Math.abs(agent.y - other.y) < 40
          );
          
          if (nearby.length > 0 && Math.random() < 0.05) {
            const target = nearby[Math.floor(Math.random() * nearby.length)];
            target.health -= Math.floor(Math.random() * 10) + 3;
            
            if (target.health <= 0) {
              target.health = 0;
              target.isDead = true;
              agent.kills++;
            }
          }
        });
        
        agentsRef.current = newAgents;
        setAliveCount(alive);
        return newAgents;
      });
    }, 100);
    
    return () => clearInterval(interval);
  }, [gameTime]);

  // Canvas rendering
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resizeCanvas = () => {
      const container = containerRef.current;
      if (!container) return;
      canvas.width = container.clientWidth;
      canvas.height = container.clientHeight;
    };
    resizeCanvas();

    // Calculate base scale for fitting
    const baseScale = calculateBaseScale();
    const currentScale = scale / baseScale; // Relative zoom level (1-20)

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      const delta = e.deltaY > 0 ? 0.9 : 1.1;
      const newScale = scale * delta;
      const minScale = baseScale * MIN_SCALE;
      const maxScaleVal = baseScale * MAX_SCALE;
      setScale(Math.max(minScale, Math.min(maxScaleVal, newScale)));
    };
    canvas.addEventListener('wheel', handleWheel, { passive: false });

    const handleMouseDown = (e: MouseEvent) => {
      setIsDragging(true);
      setDragStart({ x: e.clientX - offset.x, y: e.clientY - offset.y });
    };
    
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        setOffset({
          x: e.clientX - dragStart.x,
          y: e.clientY - dragStart.y
        });
      }
      
      const rect = canvas.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;
      setMousePos({ x: mouseX, y: mouseY });
      
      const cellSize = scale;
      const centerX = canvas.width / 2 + offset.x;
      const centerY = canvas.height / 2 + offset.y;
      const offsetX = centerX - (MAP_SIZE / 2) * cellSize;
      const offsetY = centerY - (MAP_SIZE / 2) * cellSize;
      
      let found = null;
      agentsRef.current.forEach(agent => {
        if (agent.isDead) return;
        const agentX = offsetX + agent.x * cellSize;
        const agentY = offsetY + agent.y * cellSize;
        const dist = Math.sqrt(Math.pow(mouseX - agentX, 2) + Math.pow(mouseY - agentY, 2));
        if (dist < 12 * scale / baseScale) {
          found = agent;
        }
      });
      setHoveredAgent(found);
    };
    
    const handleMouseUp = () => {
      setIsDragging(false);
    };

    canvas.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    let animationFrame: number;
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Theme colors - Cyberpunk Minimalist
      const colors = {
        bg: '#0a0a0f',
        grid: 'rgba(212, 175, 55, 0.06)',
        gridStrong: 'rgba(212, 175, 55, 0.12)',
        safeZone: 'rgba(16, 185, 129, 0.08)',
        safeZoneBorder: 'rgba(16, 185, 129, 0.5)',
        nextZone: 'rgba(16, 185, 129, 0.2)',
        poison: 'rgba(239, 68, 68, 0.12)',
        gold: '#d4af37',
        monad: '#a855f7',
        surface: 'rgba(30, 30, 35, 0.9)',
        line: 'rgba(255, 255, 255, 0.1)'
      };
      
      // Background
      ctx.fillStyle = colors.bg;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      const cellSize = scale;
      const mapPixelSize = MAP_SIZE * cellSize;
      const centerX = canvas.width / 2 + offset.x;
      const centerY = canvas.height / 2 + offset.y;
      const offsetX = centerX - mapPixelSize / 2;
      const offsetY = centerY - mapPixelSize / 2;
      
      // Calculate zoom level for detail adjustment
      const zoomLevel = scale / baseScale;
      
      // Grid - adjusts density based on zoom
      const gridSpacing = zoomLevel > 5 ? 50 : zoomLevel > 2 ? 100 : 200;
      ctx.strokeStyle = colors.grid;
      ctx.lineWidth = Math.max(0.5, 1 / zoomLevel);
      
      // Only draw visible grid lines
      const startX = Math.max(0, Math.floor((-offsetX / cellSize) / gridSpacing) * gridSpacing);
      const endX = Math.min(MAP_SIZE, Math.ceil(((-offsetX + canvas.width) / cellSize) / gridSpacing) * gridSpacing);
      const startY = Math.max(0, Math.floor((-offsetY / cellSize) / gridSpacing) * gridSpacing);
      const endY = Math.min(MAP_SIZE, Math.ceil(((-offsetY + canvas.height) / cellSize) / gridSpacing) * gridSpacing);
      
      for (let x = startX; x <= endX; x += gridSpacing) {
        ctx.beginPath();
        ctx.moveTo(offsetX + x * cellSize, Math.max(offsetY, 0));
        ctx.lineTo(offsetX + x * cellSize, Math.min(offsetY + mapPixelSize, canvas.height));
        ctx.stroke();
      }
      for (let y = startY; y <= endY; y += gridSpacing) {
        ctx.beginPath();
        ctx.moveTo(Math.max(offsetX, 0), offsetY + y * cellSize);
        ctx.lineTo(Math.min(offsetX + mapPixelSize, canvas.width), offsetY + y * cellSize);
        ctx.stroke();
      }
      
      // Stronger grid at 500 intervals
      if (zoomLevel > 2) {
        ctx.strokeStyle = colors.gridStrong;
        ctx.lineWidth = Math.max(0.8, 2 / zoomLevel);
        for (let x = startX; x <= endX; x += 500) {
          if (x % 1000 !== 0) {
            ctx.beginPath();
            ctx.moveTo(offsetX + x * cellSize, Math.max(offsetY, 0));
            ctx.lineTo(offsetX + x * cellSize, Math.min(offsetY + mapPixelSize, canvas.height));
            ctx.stroke();
          }
        }
        for (let y = startY; y <= endY; y += 500) {
          if (y % 1000 !== 0) {
            ctx.beginPath();
            ctx.moveTo(Math.max(offsetX, 0), offsetY + y * cellSize);
            ctx.lineTo(Math.min(offsetX + mapPixelSize, canvas.width), offsetY + y * cellSize);
            ctx.stroke();
          }
        }
      }
      
      // Map border
      ctx.strokeStyle = colors.gold;
      ctx.lineWidth = Math.max(1, 3 / zoomLevel);
      ctx.globalAlpha = 0.3;
      ctx.strokeRect(offsetX, offsetY, mapPixelSize, mapPixelSize);
      ctx.globalAlpha = 1;
      
      // Central structure - minimalist square
      const centerSize = 200 * cellSize;
      ctx.fillStyle = colors.surface;
      ctx.fillRect(
        offsetX + (MAP_SIZE/2 - 100) * cellSize,
        offsetY + (MAP_SIZE/2 - 100) * cellSize,
        centerSize,
        centerSize
      );
      ctx.strokeStyle = colors.gold;
      ctx.lineWidth = Math.max(1, 2 / zoomLevel);
      ctx.globalAlpha = 0.5;
      ctx.strokeRect(
        offsetX + (MAP_SIZE/2 - 100) * cellSize,
        offsetY + (MAP_SIZE/2 - 100) * cellSize,
        centerSize,
        centerSize
      );
      ctx.globalAlpha = 1;
      
      // Inner core
      const coreSize = 60 * cellSize;
      ctx.fillStyle = 'rgba(168, 85, 247, 0.3)';
      ctx.fillRect(
        offsetX + (MAP_SIZE/2 - 30) * cellSize,
        offsetY + (MAP_SIZE/2 - 30) * cellSize,
        coreSize,
        coreSize
      );
      
      // Corner platforms
      const platforms = [
        { x: 300, y: 300, size: 150 },
        { x: 1700, y: 300, size: 150 },
        { x: 300, y: 1700, size: 150 },
        { x: 1700, y: 1700, size: 150 }
      ];
      
      platforms.forEach(platform => {
        ctx.fillStyle = colors.surface;
        ctx.fillRect(
          offsetX + (platform.x - platform.size/2) * cellSize,
          offsetY + (platform.y - platform.size/2) * cellSize,
          platform.size * cellSize,
          platform.size * cellSize
        );
        ctx.strokeStyle = colors.line;
        ctx.lineWidth = Math.max(0.5, 1 / zoomLevel);
        ctx.strokeRect(
          offsetX + (platform.x - platform.size/2) * cellSize,
          offsetY + (platform.y - platform.size/2) * cellSize,
          platform.size * cellSize,
          platform.size * cellSize
        );
      });
      
      // Cross barriers
      ctx.strokeStyle = 'rgba(212, 175, 55, 0.2)';
      ctx.lineWidth = Math.max(2, 4 / zoomLevel);
      ctx.setLineDash([10 * cellSize, 10 * cellSize]);
      
      // Horizontal barriers
      ctx.beginPath();
      ctx.moveTo(offsetX + 400 * cellSize, offsetY + 1000 * cellSize);
      ctx.lineTo(offsetX + 600 * cellSize, offsetY + 1000 * cellSize);
      ctx.stroke();
      
      ctx.beginPath();
      ctx.moveTo(offsetX + 1400 * cellSize, offsetY + 1000 * cellSize);
      ctx.lineTo(offsetX + 1600 * cellSize, offsetY + 1000 * cellSize);
      ctx.stroke();
      
      // Vertical barriers
      ctx.beginPath();
      ctx.moveTo(offsetX + 1000 * cellSize, offsetY + 400 * cellSize);
      ctx.lineTo(offsetX + 1000 * cellSize, offsetY + 600 * cellSize);
      ctx.stroke();
      
      ctx.beginPath();
      ctx.moveTo(offsetX + 1000 * cellSize, offsetY + 1400 * cellSize);
      ctx.lineTo(offsetX + 1000 * cellSize, offsetY + 1600 * cellSize);
      ctx.stroke();
      
      ctx.setLineDash([]);
      
      // Next safe zone (dashed)
      ctx.fillStyle = colors.nextZone;
      ctx.strokeStyle = 'rgba(16, 185, 129, 0.4)';
      ctx.lineWidth = Math.max(1, 2 / zoomLevel);
      ctx.setLineDash([5 * cellSize, 5 * cellSize]);
      ctx.beginPath();
      ctx.arc(
        offsetX + zone.nextCenterX * cellSize,
        offsetY + zone.nextCenterY * cellSize,
        zone.nextRadius * cellSize,
        0, Math.PI * 2
      );
      ctx.fill();
      ctx.stroke();
      ctx.setLineDash([]);
      
      // Current safe zone
      const zoneGradient = ctx.createRadialGradient(
        offsetX + zone.centerX * cellSize,
        offsetY + zone.centerY * cellSize,
        0,
        offsetX + zone.centerX * cellSize,
        offsetY + zone.centerY * cellSize,
        zone.radius * cellSize
      );
      zoneGradient.addColorStop(0, colors.safeZone);
      zoneGradient.addColorStop(1, 'rgba(16, 185, 129, 0.02)');
      ctx.fillStyle = zoneGradient;
      ctx.beginPath();
      ctx.arc(
        offsetX + zone.centerX * cellSize,
        offsetY + zone.centerY * cellSize,
        zone.radius * cellSize,
        0, Math.PI * 2
      );
      ctx.fill();
      
      ctx.strokeStyle = colors.safeZoneBorder;
      ctx.lineWidth = Math.max(1, 3 / zoomLevel);
      ctx.beginPath();
      ctx.arc(
        offsetX + zone.centerX * cellSize,
        offsetY + zone.centerY * cellSize,
        zone.radius * cellSize,
        0, Math.PI * 2
      );
      ctx.stroke();
      
      // Poison zone overlay
      ctx.fillStyle = colors.poison;
      ctx.fillRect(offsetX, offsetY, mapPixelSize, mapPixelSize);
      
      // Clear safe zone from poison
      ctx.globalCompositeOperation = 'destination-out';
      ctx.beginPath();
      ctx.arc(
        offsetX + zone.centerX * cellSize,
        offsetY + zone.centerY * cellSize,
        zone.radius * cellSize,
        0, Math.PI * 2
      );
      ctx.fill();
      ctx.globalCompositeOperation = 'source-over';
      
      // Agents - minimalist dots
      agentsRef.current.forEach(agent => {
        if (agent.isDead) return;
        
        const agentX = offsetX + agent.x * cellSize;
        const agentY = offsetY + agent.y * cellSize;
        const size = Math.max(3, 5 * scale / baseScale);
        
        // Agent body
        ctx.fillStyle = agent.inSafeZone ? agent.color : '#ef4444';
        ctx.beginPath();
        ctx.arc(agentX, agentY, size / 2, 0, Math.PI * 2);
        ctx.fill();
        
        // Agent border
        ctx.strokeStyle = colors.bg;
        ctx.lineWidth = Math.max(1, 1.5 / zoomLevel);
        ctx.beginPath();
        ctx.arc(agentX, agentY, size / 2, 0, Math.PI * 2);
        ctx.stroke();
        
        // Health bar - only show when zoomed in
        if (zoomLevel > 3) {
          const barWidth = Math.max(15, 20 * scale / baseScale);
          const barHeight = Math.max(2, 3 * scale / baseScale);
          ctx.fillStyle = colors.bg;
          ctx.fillRect(agentX - barWidth / 2, agentY - size - barHeight - 2, barWidth, barHeight);
          
          const healthPercent = agent.health / agent.maxHealth;
          ctx.fillStyle = healthPercent > 0.5 ? '#10b981' : healthPercent > 0.25 ? '#f59e0b' : '#ef4444';
          ctx.fillRect(agentX - barWidth / 2, agentY - size - barHeight - 2, barWidth * healthPercent, barHeight);
        }
      });

      animationFrame = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      canvas.removeEventListener('wheel', handleWheel);
      canvas.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      cancelAnimationFrame(animationFrame);
    };
  }, [scale, offset, isDragging, dragStart, zone, agents, calculateBaseScale]);

  const timeToNextShrink = Math.max(0, SHRINK_INTERVAL - (gameTime % SHRINK_INTERVAL));
  const isShrinking = timeToNextShrink < SHRINK_DURATION;
  const zoomLevel = scale / calculateBaseScale();

  // Get top 5 agents by kills
  const topAgents = [...agents]
    .filter(a => !a.isDead)
    .sort((a, b) => b.kills - a.kills)
    .slice(0, 5);

  return (
    <div ref={containerRef} className="h-full w-full bg-[#0a0a0f] relative overflow-hidden">
      <canvas 
        ref={canvasRef} 
        className="absolute inset-0 w-full h-full cursor-grab active:cursor-grabbing" 
      />
      
      {/* UI Overlay - Minimalist Theme */}
      <div className="absolute top-4 left-4 flex items-start gap-4 pointer-events-none">
        <div className="bg-surface/90 backdrop-blur-sm rounded-lg px-4 py-2 border border-gold/30">
          <div className="text-[10px] text-gold/70 uppercase tracking-wider mb-0.5 font-semibold">Alive</div>
          <div className="text-xl font-mono font-bold text-gold">{aliveCount}</div>
        </div>
        
        <div className="bg-surface/90 backdrop-blur-sm rounded-lg px-4 py-2 border border-gold/30">
          <div className="text-[10px] text-gold/70 uppercase tracking-wider mb-0.5 font-semibold">
            {isShrinking ? 'Zone Shrinking' : 'Next Zone'}
          </div>
          <div className={`text-xl font-mono font-bold ${isShrinking ? 'text-red-400' : 'text-green-400'}`}>
            {Math.ceil(timeToNextShrink / 1000)}s
          </div>
        </div>
      </div>
      
      {/* Top 5 Ranking */}
      <div className="absolute top-4 right-4 bg-surface/90 backdrop-blur-sm rounded-lg border border-gold/30 pointer-events-auto">
        <div className="px-3 py-2 border-b border-gold/20">
          <div className="text-[10px] text-gold/70 uppercase tracking-wider font-semibold">Top 5</div>
        </div>
        <div className="p-2 space-y-1">
          {topAgents.map((agent, index) => (
            <div key={agent.id} className="flex items-center gap-2 text-xs">
              <span className="text-muted w-4">{index + 1}</span>
              <div 
                className="w-2 h-2 rounded-full" 
                style={{ backgroundColor: agent.color }}
              />
              <span className="text-ink truncate w-20">{agent.name}</span>
              <span className="text-gold ml-auto">{agent.kills}K</span>
            </div>
          ))}
          {topAgents.length === 0 && (
            <div className="text-xs text-muted py-2">No agents</div>
          )}
        </div>
      </div>
      
      {/* Agent Tooltip */}
      <AnimatePresence>
        {hoveredAgent && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="absolute pointer-events-none z-50"
            style={{
              left: mousePos.x + 12,
              top: mousePos.y - 8
            }}
          >
            <div className="bg-surface/95 backdrop-blur-sm rounded-lg px-3 py-2 border border-gold/30 shadow-lg">
              <div className="flex items-center gap-2 mb-1">
                <div 
                  className="w-2 h-2 rounded-full" 
                  style={{ backgroundColor: hoveredAgent.color }}
                />
                <span className="text-sm font-bold text-gold">{hoveredAgent.name}</span>
              </div>
              <div className="space-y-0.5 text-xs">
                <div className="flex justify-between gap-4">
                  <span className="text-muted">Health</span>
                  <span className={`font-mono ${hoveredAgent.health > 50 ? 'text-green-400' : hoveredAgent.health > 25 ? 'text-yellow-400' : 'text-red-400'}`}>
                    {Math.ceil(hoveredAgent.health)}%
                  </span>
                </div>
                <div className="flex justify-between gap-4">
                  <span className="text-muted">Kills</span>
                  <span className="font-mono text-gold">{hoveredAgent.kills}</span>
                </div>
                <div className="flex justify-between gap-4">
                  <span className="text-muted">Status</span>
                  <span className={hoveredAgent.inSafeZone ? 'text-green-400' : 'text-red-400'}>
                    {hoveredAgent.inSafeZone ? 'Safe' : 'In Poison'}
                  </span>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Legend */}
      <div className="absolute bottom-4 left-4 bg-surface/90 backdrop-blur-sm rounded-lg px-3 py-2 border border-gold/30">
        <div className="flex items-center gap-4 text-[10px]">
          <div className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 bg-green-500/80 rounded-sm" />
            <span className="text-muted">Safe Zone</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 bg-red-500/80 rounded-sm" />
            <span className="text-muted">Poison</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-2.5 h-2.5 border border-dashed border-green-500 rounded-sm" />
            <span className="text-muted">Next Zone</span>
          </div>
        </div>
      </div>
      
      {/* Controls hint */}
      <div className="absolute bottom-4 right-4 bg-surface/90 backdrop-blur-sm rounded-lg px-3 py-2 border border-gold/30">
        <div className="text-[10px] text-muted">
          Scroll to zoom · Drag to pan
        </div>
      </div>
    </div>
  );
};

export default Battlefield;
